// apps/web/src/components/shell/ao-vivo-provider.tsx — Fase 17.8, Plano 12 (D-13; UI-SPEC U-25).
// Canal ao vivo do painel: UMA conexão EventSource por aba do navegador, aberta no AppShell só para sessões com alguma permissão
// de aba do painel (GATE_BLOCO). Reconecta com backoff; depois de falhas seguidas sonda o endpoint com fetch para distinguir
// 401/403 (encerra as tentativas) de queda de rede. Distribui os `painel_invalidar` aos assinantes (o hook useAoVivo da página)
// e guarda a última atualização que a tela registrou, para o indicador da topbar. O backoff, a sondagem e a classificação vêm de
// lib/kanban/sse.ts (mesmo padrão do Kanban); a máquina de estados, de lib/painel/home/ao-vivo.ts.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import type { PainelEventoStream } from '@pieltec/contracts';
import { blocosVisiveis } from '../../lib/painel/blocos';
import { FALHAS_ANTES_DA_SONDAGEM, atrasoReconexaoMs, classificarSondagemSse } from '../../lib/kanban/sse';
import { interpretarEventoPainel, proximoEstadoAoVivo, type EstadoAoVivo, type SinalAoVivo } from '../../lib/painel/home/ao-vivo';

export const URL_STREAM_PAINEL = '/api/v1/painel/stream';

/** Intervalo do relógio interno que decide "Reconectando…" e "Sem atualização" sem depender de um novo evento. */
const INTERVALO_RELOGIO_MS = 2000;

export type AssinanteAoVivo = (evento: PainelEventoStream) => void;

export interface CanalAoVivo {
  readonly estado: EstadoAoVivo;
  /** Registra um assinante dos eventos `painel_invalidar`; devolve a função que cancela a assinatura. */
  readonly assinar: (assinante: AssinanteAoVivo) => () => void;
  /** Quando a tela atual recebeu dados pela última vez (`null` = a tela não registra e o indicador não aparece). */
  readonly ultimaAtualizacao: Date | null;
  readonly registrarAtualizacao: (data: Date | null) => void;
}

// Fora do provider (testes isolados de componentes) não há canal: nada conecta e o indicador não aparece.
const CANAL_AUSENTE: CanalAoVivo = {
  estado: 'conectando',
  assinar: () => () => undefined,
  ultimaAtualizacao: null,
  registrarAtualizacao: () => undefined,
};

const ContextoAoVivo = createContext<CanalAoVivo>(CANAL_AUSENTE);

export function useCanalAoVivo(): CanalAoVivo {
  return useContext(ContextoAoVivo);
}

export interface AoVivoProviderProps {
  /** Permissões da sessão: sem nenhuma permissão de aba do painel, não há conexão. */
  readonly permissoes: readonly string[];
  readonly children: ReactNode;
}

export function AoVivoProvider({ permissoes, children }: AoVivoProviderProps): ReactElement {
  const habilitado = useMemo(() => blocosVisiveis(permissoes).length > 0, [permissoes]);
  const [estado, setEstado] = useState<EstadoAoVivo>('conectando');
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);
  const assinantes = useRef<Set<AssinanteAoVivo>>(new Set());
  // Última atualização registrada pela tela: uma recarga que deu certo (ex.: a de 2 min) também prova que o dado está fresco, então
  // o indicador não fala em "Sem atualização" só porque o stream caiu.
  const ultimaAtualizacaoMs = useRef<number>(0);

  const assinar = useCallback((assinante: AssinanteAoVivo): (() => void) => {
    assinantes.current.add(assinante);
    return () => {
      assinantes.current.delete(assinante);
    };
  }, []);
  const registrarAtualizacao = useCallback((data: Date | null): void => {
    ultimaAtualizacaoMs.current = data === null ? 0 : data.getTime();
    setUltimaAtualizacao(data);
  }, []);

  useEffect(() => {
    if (!habilitado || typeof EventSource === 'undefined') return undefined;

    let sse: EventSource | null = null;
    let reconexao: ReturnType<typeof setTimeout> | undefined;
    let sondagem: AbortController | null = null;
    let encerrado = false;
    let falhas = 0;
    let ultimoDadoEm = Date.now();
    let primeiroErroEm: number | null = null;

    const aplicar = (sinal: SinalAoVivo): void => {
      const agora = Date.now();
      const ms = {
        desdeErro: primeiroErroEm === null ? 0 : agora - primeiroErroEm,
        desdeDados: agora - Math.max(ultimoDadoEm, ultimaAtualizacaoMs.current),
      };
      setEstado((atual) => proximoEstadoAoVivo(atual, sinal, ms));
    };

    // Depois de falhas seguidas, um GET revela o status HTTP do stream, que o EventSource esconde: 401/403 encerram as tentativas.
    const estaNaoAutorizado = async (): Promise<boolean> => {
      sondagem = new AbortController();
      try {
        const resposta = await fetch(URL_STREAM_PAINEL, {
          credentials: 'include',
          headers: { Accept: 'text/event-stream' },
          signal: sondagem.signal,
        });
        sondagem.abort(); // só o status interessa; não consome o stream
        return classificarSondagemSse(resposta.status) === 'nao_autorizado';
      } catch {
        return false;
      }
    };

    const conectar = (): void => {
      const fonte = new EventSource(URL_STREAM_PAINEL, { withCredentials: true });
      sse = fonte;

      fonte.onopen = () => {
        falhas = 0;
        primeiroErroEm = null;
        ultimoDadoEm = Date.now();
        aplicar('aberto');
      };

      fonte.onmessage = (mensagem: MessageEvent) => {
        const evento = typeof mensagem.data === 'string' ? interpretarEventoPainel(mensagem.data) : null;
        if (evento === null) return;
        ultimoDadoEm = Date.now();
        primeiroErroEm = null;
        aplicar(evento.tipo === 'ping' ? 'ping' : 'dados');
        if (evento.tipo === 'painel_invalidar') {
          for (const assinante of [...assinantes.current]) {
            try {
              assinante(evento);
            } catch {
              // um assinante com defeito não derruba os demais nem a conexão
            }
          }
        }
      };

      fonte.onerror = () => {
        fonte.close();
        falhas += 1;
        if (primeiroErroEm === null) primeiroErroEm = Date.now();
        aplicar('erro');
        const reconectar = (): void => {
          // Backoff exponencial com teto (2 s, 4 s ... 60 s). O stream também termina de propósito a cada 15 min (reconexão pelos guards).
          reconexao = setTimeout(() => {
            if (!encerrado) conectar();
          }, atrasoReconexaoMs(falhas));
        };
        if (falhas < FALHAS_ANTES_DA_SONDAGEM) {
          reconectar();
          return;
        }
        void estaNaoAutorizado().then((negado) => {
          if (encerrado) return;
          if (negado) {
            aplicar('nao-autorizado');
            return;
          }
          reconectar();
        });
      };
    };

    conectar();
    const relogio = setInterval(() => aplicar('tick'), INTERVALO_RELOGIO_MS);

    return () => {
      encerrado = true;
      clearTimeout(reconexao);
      clearInterval(relogio);
      sondagem?.abort();
      sse?.close();
    };
  }, [habilitado]);

  const canal = useMemo<CanalAoVivo>(
    () => ({ estado, assinar, ultimaAtualizacao, registrarAtualizacao }),
    [estado, assinar, ultimaAtualizacao, registrarAtualizacao],
  );

  return <ContextoAoVivo.Provider value={canal}>{children}</ContextoAoVivo.Provider>;
}
